// Der Bauplan der Blockfigur als reine Daten (docs/architektur-paket-2.md, Abschnitt „Avatar“):
// Kopf mit Gesichtsbereich, Rumpf, zwei Arme mit Drehpunkt an der Schulter und zwei Beine mit
// Drehpunkt an der Hüfte. Kein Rendering – der Client baut daraus `render/avatarModel.ts`
// (Mitspieler, Spiegel, Schulterkamera) und `render/arms.ts` (die eigenen Arme in Ego-Sicht).
//
// Konventionen wie überall: Ursprung ist die Fußpunkt-Mitte, `y` zeigt nach oben, der Avatar
// blickt bei yaw 0 nach −z (Norden), links liegt also bei −x. Alle Maße folgen aus `PLAYER`
// (0.6 breit, 1.8 hoch), damit die Figur genau in ihren Kollisionsquader passt: Ein Teil, das
// darüber hinausragte, steckte in Wänden, durch die der Avatar hindurchpasst.

import { PLAYER } from '../sim/state.js';
import type { AvatarColors } from './types.js';

/** Die sechs Teile der Figur; mehrere Quader dürfen zu demselben Teil gehören. */
export const AVATAR_TEILE = ['kopf', 'rumpf', 'arm links', 'arm rechts', 'bein links', 'bein rechts'] as const;

export type AvatarTeil = (typeof AVATAR_TEILE)[number];

/**
 * Woher ein Quader seine Farbe nimmt: einer der vier wählbaren Bereiche oder `gesicht` – das ist
 * fest dunkel und gehört niemandem zur Auswahl, sonst könnte man sich die Augen wegfärben.
 */
export type AvatarBereich = keyof AvatarColors | 'gesicht';

/**
 * Ein Quader der Figur. `size` sind die Kantenlängen in Blöcken. `center` ist der Mittelpunkt –
 * ohne `pivot` relativ zur Fußpunkt-Mitte, mit `pivot` relativ zu diesem Drehpunkt. Der Drehpunkt
 * selbst liegt relativ zur Fußpunkt-Mitte und ist der Punkt, um den das Glied beim Gehen pendelt
 * (Schulter bzw. Hüfte); alle Quader desselben Glieds nennen denselben Drehpunkt.
 */
export interface AvatarTeilQuader {
  teil: AvatarTeil;
  bereich: AvatarBereich;
  size: [number, number, number];
  center: [number, number, number];
  pivot?: [number, number, number];
}

/** Die ganze Figur als Liste von Quadern. */
export interface AvatarBauplan {
  teile: AvatarTeilQuader[];
}

/**
 * Pendelrichtung je Teil beim Gehen: +1 schwingt nach vorn (−z), −1 nach hinten, 0 steht still.
 * Diagonal gegenläufig wie bei der Kuh (ADR 0014) – linker Arm mit rechtem Bein.
 */
export const AVATAR_PENDEL: Readonly<Record<AvatarTeil, -1 | 0 | 1>> = {
  kopf: 0,
  rumpf: 0,
  'arm links': 1,
  'arm rechts': -1,
  'bein links': -1,
  'bein rechts': 1,
};

/**
 * Der Bauplan der Figur. Alle Werte sind Bruchteile von `PLAYER.width` und `PLAYER.height`;
 * die drei Abschnitte Beine (0.40), Rumpf (0.35) und Kopf (0.25) ergeben zusammen genau die
 * Körperhöhe. Die Augen sitzen auf `PLAYER.eyeHeight`, also genau dort, wo in der Ego-Sicht die
 * Kamera steht.
 */
export function avatarBauplan(): AvatarBauplan {
  const w = PLAYER.width;
  const h = PLAYER.height;

  const huefte = 0.4 * h; // Oberkante der Beine, Unterkante des Rumpfes
  const schulter = 0.72 * h; // Drehpunkt der Arme, knapp unter dem Nacken
  const nacken = 0.75 * h; // Oberkante des Rumpfes, Unterkante des Kopfes

  const kopfBreite = 0.72 * w;
  const kopfTiefe = 0.68 * w;
  const haarUeberstand = 0.02 * w; // Die Haare liegen wie eine Kappe etwas über dem Kopf.
  // Rumpf und Arme zusammen bleiben knapp unter der vollen Breite: 0.60 + 2 × 0.19 = 0.98 von 0.6.
  const rumpfBreite = 0.6 * w;
  const rumpfTiefe = 0.36 * w;
  const armDicke = 0.19 * w;
  const armX = (rumpfBreite + armDicke) / 2;
  const oberarm = 0.17 * h; // Ärmel, also Oberteil
  const unterarm = 0.17 * h; // Unterarm und Hand, also Haut; die Hand endet auf Hüfthöhe
  const beinBreite = 0.25 * w;
  const beinTiefe = 0.3 * w;
  // Etwas weiter auseinander als der halbe Beinabstand: So bleibt zwischen den Beinen ein
  // schmaler Spalt sichtbar, sonst sähe die Figur von vorn wie eine Säule aus.
  const beinX = 0.17 * w;

  const teile: AvatarTeilQuader[] = [
    // Kopf: Fleisch, Haarkappe oben, Haare hinten, zwei Augen vorn.
    {
      teil: 'kopf',
      bereich: 'haut',
      size: [kopfBreite, h - nacken, kopfTiefe],
      center: [0, (nacken + h) / 2, 0],
    },
    {
      teil: 'kopf',
      bereich: 'haar',
      size: [kopfBreite + haarUeberstand, 0.06 * h, kopfTiefe + haarUeberstand],
      center: [0, h - 0.03 * h, 0],
    },
    {
      teil: 'kopf',
      bereich: 'haar',
      size: [kopfBreite + haarUeberstand, 0.16 * h, 0.04 * w],
      center: [0, h - 0.06 * h - 0.08 * h, kopfTiefe / 2 + 0.02 * w],
    },
    ...([-1, 1] as const).map((seite): AvatarTeilQuader => ({
      teil: 'kopf',
      bereich: 'gesicht',
      size: [0.1 * w, 0.05 * h, 0.02 * w],
      center: [seite * 0.15 * w, PLAYER.eyeHeight, -(kopfTiefe / 2 + 0.01 * w)],
    })),

    // Rumpf: das Oberteil.
    {
      teil: 'rumpf',
      bereich: 'oberteil',
      size: [rumpfBreite, nacken - huefte, rumpfTiefe],
      center: [0, (huefte + nacken) / 2, 0],
    },

    // Arme: Ärmel oben, nackter Unterarm unten; beide hängen an der Schulter.
    ...([-1, 1] as const).flatMap((seite): AvatarTeilQuader[] => {
      const teil: AvatarTeil = seite === -1 ? 'arm links' : 'arm rechts';
      const pivot: [number, number, number] = [seite * armX, schulter, 0];
      return [
        { teil, bereich: 'oberteil', size: [armDicke, oberarm, armDicke], center: [0, -oberarm / 2, 0], pivot },
        {
          teil,
          bereich: 'haut',
          size: [armDicke, unterarm, armDicke],
          center: [0, -oberarm - unterarm / 2, 0],
          pivot,
        },
      ];
    }),

    // Beine: Hosenbein bis zum Boden, Drehpunkt an der Hüfte.
    ...([-1, 1] as const).map((seite): AvatarTeilQuader => ({
      teil: seite === -1 ? 'bein links' : 'bein rechts',
      bereich: 'hose',
      size: [beinBreite, huefte, beinTiefe],
      center: [0, -huefte / 2, 0],
      pivot: [seite * beinX, huefte, 0],
    })),
  ];

  return { teile };
}
